import { useListState } from "@/hooks/use-list-state";
import { useEffect, useState } from "react";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, TrendingUp, DollarSign, Home, Clock, ArrowRight, Building2, Tag, Loader2, Check, Users, Award, MessageSquare, BedDouble, Bath, CheckSquare, ChevronLeft, ChevronRight, Images, Map as MapIcon, List, ImageIcon, User, Inbox, FileText } from "lucide-react";
import { BuyerInquiriesView } from "@/components/buyer-inquiries-view";
import { ListingPhotoGallery } from "@/components/listing-photo-gallery";
import RealtorListingsMap from "@/components/realtor-listings-map";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import type { BuyDeal } from "@shared/schema";

type MarketplaceListing = {
  id: number;
  propertyId: number;
  userId: number;
  askingPrice: string;
  description: string | null;
  photos: string[];
  status: string;
  sellerName: string | null;
  propertyName: string;
  propertyAddress: string | null;
  latitude: number | null;
  longitude: number | null;
};
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";

function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null) return "—";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
}

function toNum(val: string | number | null | undefined): number {
  if (val == null) return 0;
  return typeof val === "string" ? parseFloat(val) : val;
}

// ─── Wholesaler deal card ─────────────────────────────────────────────────────

function DealCard({ deal, copiedPropertyId }: { deal: BuyDeal; copiedPropertyId?: number | null }) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const coc = toNum(deal.cashOnCash);
  const alreadyCopied = copiedPropertyId != null;
  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "buydeal", sourceListingId: deal.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check-batch"] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });
  const cocColor = coc >= 20 ? "text-green-600" : coc >= 17 ? "text-blue-600" : "text-amber-600";
  const photos = ((deal.photos ?? []).filter(Boolean) as string[]);
  const galleryPhotos = photos.length > 0 ? photos : (deal.imageUrl ? [deal.imageUrl] : []);

  return (
    <Link
      href={`/app/buy/${deal.id}`}
      className="block border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow cursor-pointer group"
      data-testid={`deal-card-${deal.id}`}
    >
      <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <ListingPhotoGallery
          photos={galleryPhotos}
          testIdPrefix={`deal-${deal.id}`}
          alt={deal.address}
          accent="blue"
          fallback={<Building2 className="h-12 w-12 text-slate-300" />}
          overlay={
            <>
              {deal.isSample && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-white/40 text-5xl font-black uppercase tracking-[0.3em] rotate-[-20deg] select-none"
                    style={{ textShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                  >
                    SAMPLE
                  </span>
                </div>
              )}
              <div className="absolute top-3 left-3">
                <Badge className="bg-white/90 text-gray-800 border-0 shadow-sm backdrop-blur-sm text-xs font-medium px-2.5 py-1">
                  <MapPin className="h-3 w-3 mr-1" />
                  {deal.market}
                </Badge>
              </div>
              <div className="absolute top-3 right-3">
                <Badge className={`${coc >= 20 ? "bg-green-500" : coc >= 17 ? "bg-blue-500" : "bg-amber-500"} text-white border-0 shadow-sm text-xs font-semibold px-2.5 py-1`}>
                  {coc}% CoC
                </Badge>
              </div>
            </>
          }
        />
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-base mb-0.5" data-testid={`deal-address-${deal.id}`}>
          {deal.address}
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          {deal.city}, {deal.state}
        </p>

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Purchase Price</p>
            <p className="text-lg font-bold text-gray-900" data-testid={`deal-price-${deal.id}`}>
              {formatCurrency(deal.purchasePrice)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Monthly Cash Flow</p>
            <p className={`text-lg font-bold ${cocColor}`} data-testid={`deal-cashflow-${deal.id}`}>
              {formatCurrency(deal.monthlyCashFlow)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-400 mb-0.5">
              <TrendingUp className="h-3 w-3" />
            </div>
            <p className="text-xs text-gray-500">Cash-on-Cash</p>
            <p className={`text-sm font-semibold ${cocColor}`}>{coc}%</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-400 mb-0.5">
              <Home className="h-3 w-3" />
            </div>
            <p className="text-xs text-gray-500">Bed / Bath</p>
            <p className="text-sm font-semibold text-gray-700">{deal.currentBedrooms} / {deal.currentBathrooms}</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-400 mb-0.5">
              <DollarSign className="h-3 w-3" />
            </div>
            <p className="text-xs text-gray-500">Cap Rate</p>
            <p className="text-sm font-semibold text-gray-700">{toNum(deal.capRate)}%</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {deal.sqft && <span>{deal.sqft.toLocaleString()} sqft</span>}
            {deal.sqft && deal.yearBuilt && <span>·</span>}
            {deal.yearBuilt && <span>Built {deal.yearBuilt}</span>}
          </div>
          <span className="text-xs text-blue-500 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            Details <ArrowRight className="h-3 w-3" />
          </span>
        </div>

        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="mt-3">
          <Button
            className="w-full"
            variant={alreadyCopied ? "default" : "outline"}
            size="sm"
            onClick={() => {
              if (alreadyCopied && copiedPropertyId) {
                navigate(`/app/underwriting/${copiedPropertyId}`);
              } else {
                copyMutation.mutate();
              }
            }}
            disabled={copyMutation.isPending}
            data-testid={`button-underwrite-deal-${deal.id}`}
          >
            {copyMutation.isPending ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Copying...</>
            ) : alreadyCopied ? (
              "Review in Underwriting"
            ) : (
              "Copy to Underwriting"
            )}
          </Button>
        </div>
      </div>
    </Link>
  );
}

interface WholesaleListing {
  id: number;
  address: string;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  askingPrice: string;
  arv: string | null;
  bedrooms: number | null;
  bathrooms: string | null;
  sqft: number | null;
  yearBuilt: number | null;
  description: string | null;
  photos: string[];
  sellerFinancing: boolean;
  interestRate: string | null;
  termYears: number | null;
  downPaymentPercent: string | null;
  estimatedDaysAvailable: number | null;
  wholesalerName: string | null;
  wholesalerCompany: string | null;
}

function WholesaleCard({ listing, onInterested, copiedPropertyId }: { listing: WholesaleListing; onInterested: (l: WholesaleListing) => void; copiedPropertyId?: number | null }) {
  const queryClient = useQueryClient();
  const galleryPhotos = (listing.photos ?? []).filter(Boolean) as string[];
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const alreadyCopied = copiedPropertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "wholesaler", sourceListingId: listing.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check-batch"] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  return (
    <Link
      href={`/app/buy/wholesaler/${listing.id}`}
      className="block border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow group cursor-pointer"
      data-testid={`wholesale-card-${listing.id}`}
    >
      <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <ListingPhotoGallery
        photos={galleryPhotos}
        testIdPrefix={`wholesale-${listing.id}`}
        alt={listing.address}
        accent="blue"
        fallback={<Building2 className="h-12 w-12 text-slate-300" />}
        overlay={
          <>
            <div className="absolute top-3 left-3 flex gap-1.5">
              {listing.city && (
                <Badge className="bg-white/90 text-gray-800 border-0 shadow-sm backdrop-blur-sm text-xs font-medium px-2.5 py-1">
                  <MapPin className="h-3 w-3 mr-1" />
                  {listing.city}{listing.state ? `, ${listing.state}` : ""}
                </Badge>
              )}
            </div>
            <div className="absolute top-3 right-3">
              <Badge className="bg-amber-500 text-white border-0 shadow-sm text-xs font-semibold px-2.5 py-1 flex items-center gap-1">
                <Tag className="h-3 w-3" />
                Wholesale
              </Badge>
            </div>
            {listing.sellerFinancing && (
              <div className="absolute bottom-3 left-3">
                <Badge className="bg-green-500 text-white border-0 text-xs">Seller Financing</Badge>
              </div>
            )}
          </>
        }
      />
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-base mb-0.5" data-testid={`wholesale-address-${listing.id}`}>
          {listing.address}
        </h3>
        {(listing.city || listing.state) && (
          <p className="text-sm text-gray-500 mb-2">
            {[listing.city, listing.state, listing.zipCode].filter(Boolean).join(", ")}
          </p>
        )}

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Asking Price</p>
            <p className="text-lg font-bold text-gray-900" data-testid={`wholesale-price-${listing.id}`}>
              {formatCurrency(listing.askingPrice)}
            </p>
          </div>
          {listing.arv && (
            <div className="text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wide">ARV</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(listing.arv)}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 mb-3">
          <div className="text-center">
            <p className="text-xs text-gray-500">Beds</p>
            <p className="text-sm font-semibold text-gray-700">{listing.bedrooms ?? "—"}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Baths</p>
            <p className="text-sm font-semibold text-gray-700">{listing.bathrooms ?? "—"}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Sq Ft</p>
            <p className="text-sm font-semibold text-gray-700">{listing.sqft ? listing.sqft.toLocaleString() : "—"}</p>
          </div>
        </div>

        {listing.wholesalerCompany && (
          <p className="text-xs text-gray-400 mb-3">Source: {listing.wholesalerCompany}</p>
        )}

        <div className="flex flex-col gap-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <Button
            className="w-full"
            variant="outline"
            size="sm"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onInterested(listing); }}
            data-testid={`button-interested-${listing.id}`}
          >
            I'm Interested
          </Button>
          <Button
            className="w-full"
            variant={alreadyCopied ? "default" : "outline"}
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (alreadyCopied && copiedPropertyId) {
                navigate(`/app/underwriting/${copiedPropertyId}`);
              } else {
                copyMutation.mutate();
              }
            }}
            disabled={copyMutation.isPending}
            data-testid={`button-underwrite-myself-${listing.id}`}
          >
            {copyMutation.isPending ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Copying...</>
            ) : alreadyCopied ? (
              "Review in Underwriting"
            ) : (
              "Underwrite myself"
            )}
          </Button>
        </div>
      </div>
    </Link>
  );
}

function DealCardSkeleton() {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-tour="buy-listings">
      <Skeleton className="w-full h-48" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex justify-between">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );
}

const MARKETPLACE_STATUS_LABEL: Record<string, string> = {
  active: "Active",
  under_contract: "Under Contract",
  sold: "Sold",
};

const MARKETPLACE_STATUS_COLOR: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  under_contract: "bg-amber-100 text-amber-800 border-amber-200",
  sold: "bg-gray-100 text-gray-700 border-gray-200",
};

function MarketplaceListingCard({ listing, copiedPropertyId }: { listing: MarketplaceListing; copiedPropertyId?: number | null }) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const cover = listing.photos?.[0];
  const status = listing.status;
  const alreadyCopied = copiedPropertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "marketplace", sourceListingId: listing.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check-batch"] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  return (
    <Link
      href={`/app/buy/listing/${listing.id}`}
      className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow flex flex-col cursor-pointer"
      data-testid={`marketplace-card-${listing.id}`}
    >
      <div className="relative bg-gray-100 h-48 flex items-center justify-center">
        {cover ? (
          <img src={cover} alt={listing.propertyName} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="h-10 w-10 text-gray-300" />
        )}
        <div className="absolute top-3 left-3">
          <Badge className={`${MARKETPLACE_STATUS_COLOR[status] || MARKETPLACE_STATUS_COLOR.active} border`} data-testid={`marketplace-badge-status-${listing.id}`}>
            {MARKETPLACE_STATUS_LABEL[status] || status}
          </Badge>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-gray-900 text-base mb-1" data-testid={`marketplace-listing-name-${listing.id}`}>
          {listing.propertyName || `Listing #${listing.id}`}
        </h3>
        {listing.propertyAddress && (
          <p className="text-sm text-gray-500 mb-3 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {listing.propertyAddress}
          </p>
        )}

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Asking Price</p>
            <p className="text-xl font-bold text-gray-900" data-testid={`marketplace-listing-price-${listing.id}`}>
              {formatCurrency(listing.askingPrice)}
            </p>
          </div>
          {listing.sellerName && (
            <div className="text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Seller</p>
              <p className="text-sm font-medium text-gray-700 flex items-center gap-1 justify-end">
                <User className="h-3.5 w-3.5" /> {listing.sellerName}
              </p>
            </div>
          )}
        </div>

        {listing.description && (
          <p className="text-sm text-gray-600 line-clamp-3 mb-3">{listing.description}</p>
        )}

        <div className="border-t border-gray-100 pt-3 mt-auto flex items-center justify-between gap-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <Button size="sm" variant="outline" onClick={() => navigate(`/app/buy/listing/${listing.id}`)} data-testid={`button-view-marketplace-${listing.id}`}>
            View listing
          </Button>
          <Button
            size="sm"
            variant={alreadyCopied ? "default" : "outline"}
            onClick={() => {
              if (alreadyCopied && copiedPropertyId) {
                navigate(`/app/underwriting/${copiedPropertyId}`);
              } else {
                copyMutation.mutate();
              }
            }}
            disabled={copyMutation.isPending}
            data-testid={`button-underwrite-marketplace-${listing.id}`}
          >
            {copyMutation.isPending ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Copying...</>
            ) : alreadyCopied ? (
              "Review in Underwriting"
            ) : (
              "Underwrite myself"
            )}
          </Button>
        </div>
      </div>
    </Link>
  );
}

function InterestedDialog({
  listing,
  onClose,
}: {
  listing: WholesaleListing | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { data: copyCheck } = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/underwriting/copy-check", "wholesaler", listing?.id],
    queryFn: async () => {
      if (!listing) return { propertyId: null };
      const res = await fetch(`/api/app/underwriting/copy-check?sourceType=wholesaler&sourceListingId=${listing.id}`, { credentials: "include" });
      return res.json();
    },
    enabled: !!listing,
  });
  const alreadyCopied = copyCheck?.propertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      if (!listing) throw new Error("No listing");
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "wholesaler", sourceListingId: listing.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check", "wholesaler", listing?.id] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!listing) return;
      const res = await fetch(`/api/app/wholesaler-listings/${listing.id}/interested`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to send interest");
      }
      return res.json();
    },
    onSuccess: () => setSubmitted(true),
    onError: (err: any) => toast({ title: err.message || "Could not send interest", variant: "destructive" }),
  });

  const handleClose = () => {
    setMessage("");
    setSubmitted(false);
    onClose();
  };

  const dialogPhotos = (listing?.photos ?? []).filter(Boolean) as string[];

  return (
    <Dialog open={!!listing} onOpenChange={open => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{submitted ? "Interest Sent!" : "Express Interest"}</DialogTitle>
          <DialogDescription>{listing?.address}</DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-6 space-y-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-sm text-slate-700 font-medium">Your interest has been sent to the wholesaler.</p>
            <p className="text-xs text-slate-500">You'll receive a message in your inbox when they respond.</p>
            <Button className="mt-2" onClick={handleClose} data-testid="button-close-interest">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {listing && dialogPhotos.length > 0 && (
              <div className="-mx-6 sm:mx-0">
                <ListingPhotoGallery
                  photos={dialogPhotos}
                  testIdPrefix={`interest-${listing.id}`}
                  alt={listing.address}
                  accent="blue"
                  heroHeight="h-40"
                  rounded
                  thumbnailContainerClassName="px-1 pt-2"
                />
              </div>
            )}
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 space-y-1">
              <p className="font-medium">{listing?.address}</p>
              <p className="text-slate-500">{formatCurrency(listing?.askingPrice ?? 0)} asking{listing?.arv ? ` · ARV ${formatCurrency(listing.arv)}` : ""}</p>
              {listing?.sellerFinancing && <p className="text-green-600 font-medium">✓ Seller financing available</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Message to wholesaler (optional)</label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Tell the wholesaler why you're interested, your timeline, or any questions..."
                rows={4}
                data-testid="textarea-interest-message"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                className="flex-1"
                data-testid="button-send-interest"
              >
                {mutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Send Interest
              </Button>
              <Button variant="ghost" onClick={handleClose}>Cancel</Button>
            </div>
            <div className="pt-2 border-t border-slate-200">
              <Button
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => alreadyCopied ? navigate(`/app/underwriting/${copyCheck!.propertyId}`) : copyMutation.mutate()}
                disabled={copyMutation.isPending}
                data-testid="button-copy-to-underwriting-dialog"
              >
                {copyMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {alreadyCopied ? "Review in Underwriting" : "Copy to Underwriting"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Realtor listing card ─────────────────────────────────────────────────────

interface RealtorListing {
  id: number;
  address: string;
  city: string | null;
  state: string | null;
  price: string;
  arv: string | null;
  bedrooms: number | null;
  bathrooms: string | null;
  sqft: number | null;
  description: string | null;
  photos: string[];
  listingType: string | null;
  colivingSuitability: string | null;
  buyerAgentCommission: string | null;
  sellerFinancing: boolean;
  partnerId: number;
  partnerName?: string;
  latitude: string | number | null;
  longitude: string | number | null;
  coverPhotoUrl: string | null;
  propertyReportUrl: string | null;
  bedroomsAfterConversion: number | null;
  bathroomsAfterConversion: string | null;
  irr10Year: string | null;
  cashOnCashReturn: string | null;
  cashOutlay: string | null;
  monthlyCashFlow: string | null;
  dealType: string | null;
  dateListed: string | null;
}

const COLIVING_LABELS: Record<string, string> = {
  yes_already_operating: "Co-living active",
  yes_suitable_not_converted: "Co-living suitable",
  needs_conversion: "Needs conversion",
  unsure: "Unknown",
};

const LISTING_TYPE_LABELS: Record<string, string> = {
  mls: "MLS",
  off_market: "Off Market",
  pocket: "Pocket",
};

function RealtorListingCard({ listing, copiedPropertyId }: { listing: RealtorListing; copiedPropertyId?: number | null }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [showRequest, setShowRequest] = useState(false);
  const [message, setMessage] = useState("");
  const rawPhotos = (listing.photos ?? []).filter(Boolean);
  // coverPhotoUrl must always be the hero/first image; prepend it to the gallery
  // only if it is not already present in the photos array.
  const photos = listing.coverPhotoUrl && !rawPhotos.includes(listing.coverPhotoUrl)
    ? [listing.coverPhotoUrl, ...rawPhotos]
    : rawPhotos;
  const hasPhotos = photos.length > 0;
  const hasMany = photos.length > 1;
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const realtorAlreadyCopied = copiedPropertyId != null;

  const realtorCopyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "realtor", sourceListingId: listing.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check-batch"] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });
  const heroPhoto = listing.coverPhotoUrl || (photos.length > 0 ? photos[0] : null);
  const safeIdx = Math.min(photoIdx, Math.max(photos.length - 1, 0));
  const goPrev = () => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  const goNext = () => setPhotoIdx((i) => (i + 1) % photos.length);

  const requestMutation = useMutation({
    mutationFn: async (msg: string) => {
      const res = await apiRequest("POST", `/api/app/realtor-listings/${listing.id}/request-showing`, { message: msg });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Showing request sent", description: "The realtor will confirm your appointment." });
      setShowRequest(false);
      setMessage("");
      navigate("/app/partner-conversations");
    },
    onError: () => toast({ title: "Failed to send request", variant: "destructive" }),
  });

  const initials = (listing.partnerName || "R")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <>
      <Link
        href={`/app/realtor-listings/${listing.id}`}
        className="block border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow cursor-pointer"
        data-testid={`realtor-listing-card-${listing.id}`}
      >
        <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 h-48 flex items-center justify-center group/hero">
          {heroPhoto ? (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLightboxOpen(true); }}
              className="w-full h-full block"
              aria-label="Open photo gallery"
              data-testid={`button-open-gallery-${listing.id}`}
            >
              <img src={hasPhotos ? photos[safeIdx] : heroPhoto} alt={`${listing.address} photo ${safeIdx + 1}`} className="w-full h-full object-cover" />
            </button>
          ) : (
            <Home className="h-12 w-12 text-slate-300" />
          )}
          {hasMany && (
            <>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); goPrev(); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow opacity-0 group-hover/hero:opacity-100 transition-opacity"
                aria-label="Previous photo"
                data-testid={`button-prev-photo-${listing.id}`}
              >
                <ChevronLeft className="h-4 w-4 text-gray-800" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); goNext(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow opacity-0 group-hover/hero:opacity-100 transition-opacity"
                aria-label="Next photo"
                data-testid={`button-next-photo-${listing.id}`}
              >
                <ChevronRight className="h-4 w-4 text-gray-800" />
              </button>
              <div
                className="absolute bottom-2 right-2 bg-black/60 text-white text-xs font-medium rounded-full px-2 py-0.5 flex items-center gap-1"
                data-testid={`text-photo-count-${listing.id}`}
              >
                <Images className="h-3 w-3" />
                {safeIdx + 1} / {photos.length}
              </div>
            </>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <Badge className="bg-violet-500 text-white border-0 shadow-sm text-xs font-semibold px-2.5 py-1">
              Realtor
            </Badge>
            {listing.listingType && (
              <Badge className="bg-white/90 text-gray-800 border-0 shadow-sm text-xs font-medium px-2 py-0.5">
                {LISTING_TYPE_LABELS[listing.listingType] || listing.listingType}
              </Badge>
            )}
          </div>
        </div>

        {hasMany && (
          <div className="flex gap-1.5 px-4 pt-3 overflow-x-auto" data-testid={`thumbnail-strip-${listing.id}`}>
            {photos.slice(0, 6).map((p, i) => (
              <button
                key={`${p}-${i}`}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPhotoIdx(i); }}
                className={`flex-shrink-0 h-12 w-16 rounded overflow-hidden border-2 transition-all ${
                  i === safeIdx ? "border-violet-500" : "border-transparent opacity-70 hover:opacity-100"
                }`}
                aria-label={`Show photo ${i + 1}`}
                data-testid={`button-thumb-${listing.id}-${i}`}
              >
                <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
            {photos.length > 6 && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLightboxOpen(true); }}
                className="flex-shrink-0 h-12 w-16 rounded bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 flex items-center justify-center"
                data-testid={`button-more-photos-${listing.id}`}
              >
                +{photos.length - 6}
              </button>
            )}
          </div>
        )}

        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-base mb-0.5" data-testid={`realtor-listing-address-${listing.id}`}>
            {listing.address}
          </h3>
          {(listing.city || listing.state) && (
            <p className="text-sm text-gray-500 mb-2">{[listing.city, listing.state].filter(Boolean).join(", ")}</p>
          )}
          <p className="text-xl font-bold text-gray-900 mb-3" data-testid={`realtor-listing-price-${listing.id}`}>
            {formatCurrency(listing.price)}
          </p>

          <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
            {listing.bedrooms && (
              <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{listing.bedrooms} bd</span>
            )}
            {listing.bathrooms && (
              <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{listing.bathrooms} ba</span>
            )}
            {listing.sqft && (
              <span className="flex items-center gap-1"><Home className="h-3.5 w-3.5" />{listing.sqft.toLocaleString()} sqft</span>
            )}
          </div>

          {listing.colivingSuitability && (
            <div className="flex items-center gap-1 mb-3">
              <CheckSquare className="h-3.5 w-3.5 text-green-500" />
              <span className="text-xs text-green-700">{COLIVING_LABELS[listing.colivingSuitability] || listing.colivingSuitability}</span>
            </div>
          )}

          {(listing.monthlyCashFlow || listing.cashOnCashReturn || listing.irr10Year || listing.cashOutlay || listing.dealType || listing.bedroomsAfterConversion) && (
            <div className="border border-violet-100 rounded-lg p-2.5 bg-violet-50 mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Deal Highlights</p>
                {listing.dealType && (
                  <Badge className="text-xs bg-violet-100 text-violet-700 border-0 px-2 py-0.5">{listing.dealType}</Badge>
                )}
              </div>
              {(listing.monthlyCashFlow || listing.cashOnCashReturn || listing.irr10Year || listing.cashOutlay) && (
                <div className="grid grid-cols-2 gap-1 mb-1.5">
                  {listing.monthlyCashFlow && (
                    <div>
                      <p className="text-[10px] text-gray-500">Mo. Cash Flow</p>
                      <p className="text-xs font-semibold text-green-700">{formatCurrency(listing.monthlyCashFlow)}</p>
                    </div>
                  )}
                  {listing.cashOutlay && (
                    <div>
                      <p className="text-[10px] text-gray-500">Cash Outlay</p>
                      <p className="text-xs font-semibold text-gray-800">{formatCurrency(listing.cashOutlay)}</p>
                    </div>
                  )}
                  {listing.cashOnCashReturn && (
                    <div>
                      <p className="text-[10px] text-gray-500">CoC Return</p>
                      <p className="text-xs font-semibold text-gray-800">{(parseFloat(listing.cashOnCashReturn) * 100).toFixed(1)}%</p>
                    </div>
                  )}
                  {listing.irr10Year && (
                    <div>
                      <p className="text-[10px] text-gray-500">10-yr IRR</p>
                      <p className="text-xs font-semibold text-gray-800">{(parseFloat(listing.irr10Year) * 100).toFixed(1)}%</p>
                    </div>
                  )}
                </div>
              )}
              {(listing.bedroomsAfterConversion != null || listing.bathroomsAfterConversion != null) && (
                <p className="text-[10px] text-violet-600">
                  After conversion: {listing.bedroomsAfterConversion ?? "?"} bd / {listing.bathroomsAfterConversion ?? "?"} ba
                </p>
              )}
              {listing.propertyReportUrl && (
                <a
                  href={listing.propertyReportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-violet-600 hover:text-violet-800 underline underline-offset-2"
                  data-testid={`link-property-report-${listing.id}`}
                >
                  <FileText className="h-3 w-3" />
                  Property Report
                </a>
              )}
              {listing.dateListed && (
                <p className="text-[10px] text-gray-400 mt-1">Listed {listing.dateListed}</p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 text-xs font-bold">{initials}</div>
            <span>{listing.partnerName || "Realtor Partner"}</span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowRequest(true); }}
                data-testid={`button-request-showing-${listing.id}`}
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1" />
                Request Showing
              </Button>
              <Button
                size="sm"
                className="flex-1 bg-violet-600 hover:bg-violet-700"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/app/realtor-listings/${listing.id}`); }}
                data-testid={`button-view-details-${listing.id}`}
              >
                View Details
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            <Button
              size="sm"
              variant={realtorAlreadyCopied ? "default" : "outline"}
              className="w-full"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (realtorAlreadyCopied && copiedPropertyId) {
                  navigate(`/app/underwriting/${copiedPropertyId}`);
                } else {
                  realtorCopyMutation.mutate();
                }
              }}
              disabled={realtorCopyMutation.isPending}
              data-testid={`button-underwrite-myself-realtor-${listing.id}`}
            >
              {realtorCopyMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Copying...</>
              ) : realtorAlreadyCopied ? (
                "Review in Underwriting"
              ) : (
                "Underwrite myself"
              )}
            </Button>
          </div>
        </div>
      </Link>

      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a showing</DialogTitle>
            <DialogDescription>{listing.address} · {formatCurrency(listing.price)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor={`showing-msg-${listing.id}`}>Your message</Label>
              <Textarea
                id={`showing-msg-${listing.id}`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Hi, I'd like to schedule a showing for..."
                data-testid="input-showing-message"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => { if (message.trim()) requestMutation.mutate(message.trim()); }}
              disabled={!message.trim() || requestMutation.isPending}
              data-testid="button-send-showing"
            >
              {requestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-5xl p-0 bg-black border-0 overflow-hidden"
          data-testid={`lightbox-${listing.id}`}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{listing.address} photos</DialogTitle>
            <DialogDescription>Photo {safeIdx + 1} of {photos.length}</DialogDescription>
          </DialogHeader>
          <div className="relative w-full bg-black flex items-center justify-center" style={{ minHeight: "60vh" }}>
            {hasPhotos && (
              <img
                src={photos[safeIdx]}
                alt={`${listing.address} photo ${safeIdx + 1}`}
                className="max-h-[80vh] max-w-full object-contain"
                data-testid={`lightbox-image-${listing.id}`}
              />
            )}
            {hasMany && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Previous photo"
                  data-testid={`button-lightbox-prev-${listing.id}`}
                >
                  <ChevronLeft className="h-5 w-5 text-gray-900" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Next photo"
                  data-testid={`button-lightbox-next-${listing.id}`}
                >
                  <ChevronRight className="h-5 w-5 text-gray-900" />
                </button>
              </>
            )}
            <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-medium rounded-full px-3 py-1">
              {safeIdx + 1} / {photos.length}
            </div>
          </div>
          {hasMany && (
            <div className="flex gap-2 p-3 bg-black/95 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={`lb-${p}-${i}`}
                  type="button"
                  onClick={() => setPhotoIdx(i)}
                  className={`flex-shrink-0 h-14 w-20 rounded overflow-hidden border-2 transition-all ${
                    i === safeIdx ? "border-violet-400" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Show photo ${i + 1}`}
                  data-testid={`button-lightbox-thumb-${listing.id}-${i}`}
                >
                  <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Main Buy page ─────────────────────────────────────────────────────────────

type SourceFilter = "all" | "wholesale" | "realtor" | "marketplace" | "inquiries";

export default function AppBuy() {
  usePageTitle("Buy Deals");
  const [location, navigate] = useLocation();
  const search = useSearch();
  const initialParams = typeof window !== "undefined" ? new URLSearchParams(search) : new URLSearchParams();
  const highlightId = initialParams.get("highlight") ? parseInt(initialParams.get("highlight")!, 10) : null;
  const initialInquiryId = initialParams.get("inquiry") ? parseInt(initialParams.get("inquiry")!, 10) : null;
  const [interestedListing, setInterestedListing] = useState<WholesaleListing | null>(null);
  const [sourceFilter, setSourceFilter] = useListState<SourceFilter>("buy:source",
    initialParams.get("tab") === "inquiries" ? "inquiries" : "all",
  );

  const { data: meResp } = useQuery<{ user: { id: number; email: string; name: string | null } }>({
    queryKey: ["/api/app/auth/me"],
  });
  const currentUserId = meResp?.user?.id ?? null;

  const { data: inquiriesForBadge } = useQuery<Array<{ id: number; buyerId: number; unreadCount: number }>>({
    queryKey: ["/api/app/sell/inquiries"],
    refetchInterval: 15000,
  });
  const myInquiryUnread = currentUserId
    ? (inquiriesForBadge || []).filter((i) => i.buyerId === currentUserId).reduce((sum, i) => sum + (i.unreadCount || 0), 0)
    : 0;
  const [realtorView, setRealtorView] = useState<"list" | "map">("list");
  const [realtorCityFilter, setRealtorCityFilter] = useState<string>(
    initialParams.get("city") || "all",
  );
  const [realtorMinPrice, setRealtorMinPrice] = useState<string>(
    initialParams.get("minPrice") || "",
  );
  const [realtorMaxPrice, setRealtorMaxPrice] = useState<string>(
    initialParams.get("maxPrice") || "",
  );
  const [realtorMinBeds, setRealtorMinBeds] = useState<string>(
    initialParams.get("minBeds") || "any",
  );
  const [realtorMinBaths, setRealtorMinBaths] = useState<string>(
    initialParams.get("minBaths") || "any",
  );
  const [realtorListingType, setRealtorListingType] = useState<string>(
    initialParams.get("listingType") || "all",
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (realtorCityFilter && realtorCityFilter !== "all") {
      params.set("city", realtorCityFilter);
    } else {
      params.delete("city");
    }
    if (realtorMinPrice) {
      params.set("minPrice", realtorMinPrice);
    } else {
      params.delete("minPrice");
    }
    if (realtorMaxPrice) {
      params.set("maxPrice", realtorMaxPrice);
    } else {
      params.delete("maxPrice");
    }
    if (realtorMinBeds && realtorMinBeds !== "any") {
      params.set("minBeds", realtorMinBeds);
    } else {
      params.delete("minBeds");
    }
    if (realtorMinBaths && realtorMinBaths !== "any") {
      params.set("minBaths", realtorMinBaths);
    } else {
      params.delete("minBaths");
    }
    if (realtorListingType && realtorListingType !== "all") {
      params.set("listingType", realtorListingType);
    } else {
      params.delete("listingType");
    }
    const qs = params.toString();
    const newPath = qs ? `${location}?${qs}` : location;
    const currentPath = `${location}${window.location.search}`;
    if (newPath !== currentPath) {
      navigate(newPath, { replace: true });
    }
  }, [
    realtorCityFilter,
    realtorMinPrice,
    realtorMaxPrice,
    realtorMinBeds,
    realtorMinBaths,
    realtorListingType,
    location,
    navigate,
  ]);

  const { data: deals, isLoading: dealsLoading } = useQuery<BuyDeal[]>({
    queryKey: ["/api/app/buy-deals"],
  });

  const { data: wholesaleListings, isLoading: wholesaleLoading } = useQuery<WholesaleListing[]>({
    queryKey: ["/api/app/wholesaler-listings"],
    queryFn: async () => {
      const r = await fetch("/api/app/wholesaler-listings", { credentials: "include" });
      if (!r.ok) {
        if (r.status === 401 || r.status === 403) return [];
        throw new Error(`Failed to load wholesaler listings: ${r.status}`);
      }
      return r.json();
    },
  });

  const { data: realtorListings, isLoading: realtorLoading } = useQuery<RealtorListing[]>({
    queryKey: ["/api/app/realtor-listings"],
  });

  const { data: marketplaceListings, isLoading: marketplaceLoading } = useQuery<MarketplaceListing[]>({
    queryKey: ["/api/app/sell/marketplace"],
  });

  const wholesaleIds = (wholesaleListings ?? []).map((l) => ({ sourceType: "wholesaler" as const, sourceListingId: l.id }));
  const realtorIds = (realtorListings ?? []).map((l) => ({ sourceType: "realtor" as const, sourceListingId: l.id }));
  const buyDealIds = (deals ?? []).map((d) => ({ sourceType: "buydeal" as const, sourceListingId: d.id }));
  const marketplaceIds = (marketplaceListings ?? []).map((l) => ({ sourceType: "marketplace" as const, sourceListingId: l.id }));
  const batchItems = [...wholesaleIds, ...realtorIds, ...buyDealIds, ...marketplaceIds];
  const { data: copyCheckBatch } = useQuery<{ results: Record<string, number | null> }>({
    queryKey: ["/api/app/underwriting/copy-check-batch", batchItems.map((i) => `${i.sourceType}:${i.sourceListingId}`).join(",")],
    queryFn: async () => {
      const res = await fetch("/api/app/underwriting/copy-check-batch", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: batchItems }),
      });
      return res.json();
    },
    enabled: batchItems.length > 0,
  });
  const batchResults = copyCheckBatch?.results ?? {};

  // Auto-open InterestedDialog when navigated back via ?highlight=<id> from attribution banner
  useEffect(() => {
    if (!highlightId || !wholesaleListings || interestedListing) return;
    const match = wholesaleListings.find(l => l.id === highlightId);
    if (match) {
      setSourceFilter("wholesale");
      setInterestedListing(match);
      // Remove the highlight param to avoid re-triggering on subsequent renders
      const params = new URLSearchParams(window.location.search);
      params.delete("highlight");
      const qs = params.toString();
      navigate(qs ? `/app/buy?${qs}` : "/app/buy", { replace: true });
    }
  }, [highlightId, wholesaleListings]);

  const isLoading = dealsLoading || wholesaleLoading || realtorLoading || marketplaceLoading;
  const uniqueMarkets = deals ? Array.from(new Set(deals.map(d => d.market))) : [];

  const showInquiries = sourceFilter === "inquiries";
  const showWholesale = !showInquiries && (sourceFilter === "all" || sourceFilter === "wholesale");
  const showRealtor = !showInquiries && (sourceFilter === "all" || sourceFilter === "realtor");
  const showMarketplace = !showInquiries && (sourceFilter === "all" || sourceFilter === "marketplace");

  const realtorCityOptions = realtorListings
    ? Array.from(
        new Set(
          realtorListings
            .map((l) => [l.city, l.state].filter(Boolean).join(", "))
            .filter((s) => s.length > 0),
        ),
      ).sort()
    : [];

  const minPriceNum = realtorMinPrice ? parseFloat(realtorMinPrice) : null;
  const maxPriceNum = realtorMaxPrice ? parseFloat(realtorMaxPrice) : null;

  const minBedsNum = realtorMinBeds !== "any" ? parseInt(realtorMinBeds, 10) : null;
  const minBathsNum = realtorMinBaths !== "any" ? parseFloat(realtorMinBaths) : null;

  const filteredRealtorListings = (realtorListings ?? []).filter((l) => {
    if (realtorCityFilter !== "all") {
      const label = [l.city, l.state].filter(Boolean).join(", ");
      if (label !== realtorCityFilter) return false;
    }
    const priceNum = typeof l.price === "string" ? parseFloat(l.price) : (l.price as number);
    if (minPriceNum != null && Number.isFinite(minPriceNum) && (!Number.isFinite(priceNum) || priceNum < minPriceNum)) return false;
    if (maxPriceNum != null && Number.isFinite(maxPriceNum) && (!Number.isFinite(priceNum) || priceNum > maxPriceNum)) return false;
    if (minBedsNum != null) {
      if (l.bedrooms == null || l.bedrooms < minBedsNum) return false;
    }
    if (minBathsNum != null) {
      const bathsNum = l.bathrooms == null ? NaN : (typeof l.bathrooms === "string" ? parseFloat(l.bathrooms) : (l.bathrooms as number));
      if (!Number.isFinite(bathsNum) || bathsNum < minBathsNum) return false;
    }
    if (realtorListingType !== "all") {
      if ((l.listingType ?? "") !== realtorListingType) return false;
    }
    return true;
  });

  const realtorFiltersActive =
    realtorCityFilter !== "all" ||
    realtorMinPrice !== "" ||
    realtorMaxPrice !== "" ||
    realtorMinBeds !== "any" ||
    realtorMinBaths !== "any" ||
    realtorListingType !== "all";

  const clearRealtorFilters = () => {
    setRealtorCityFilter("all");
    setRealtorMinPrice("");
    setRealtorMaxPrice("");
    setRealtorMinBeds("any");
    setRealtorMinBaths("any");
    setRealtorListingType("all");
  };

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Home className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="text-buy-heading" data-tour="buy-heading">Buy</h1>
                <p className="text-sm text-gray-500">Browse all properties available to buy — host listings, wholesale deals, and partner realtors</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                asChild
                data-testid="button-find-realtor"
                data-tour="buy-find-realtor"
              >
                <Link href="/app/find-realtor">
                  <Users className="h-4 w-4 mr-2" />
                  Find a Realtor
                </Link>
              </Button>
              <div className="flex items-center gap-2">
                <ExplainerVideoButton pageKey="buy" />
                <GuideButton pageKey="buy" />
              </div>
            </div>
            <GuideNudgeBanner pageKey="buy" />
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="source-filter-bar" data-tour="buy-filters">
            {(["all", "marketplace", "wholesale", "realtor", "inquiries"] as SourceFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setSourceFilter(f)}
                data-testid={`filter-source-${f}`}
                {...(f === "inquiries" ? { "data-tour": "buy-inquiries-filter" } : f === "wholesale" ? { "data-tour": "buy-wholesale-section" } : {})}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  sourceFilter === f
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {f === "inquiries" && <Inbox className="h-3.5 w-3.5" />}
                {f === "all"
                  ? "All Sources"
                  : f === "marketplace"
                  ? "PadPulse Marketplace"
                  : f === "wholesale"
                  ? "Wholesale Deals"
                  : f === "realtor"
                  ? "Realtor Listings"
                  : "My Inquiries"}
                {f === "inquiries" && myInquiryUnread > 0 && (
                  <Badge
                    className={`${sourceFilter === f ? "bg-white text-blue-600" : "bg-blue-600 text-white"} border-0 text-[10px] px-1.5 py-0`}
                    data-testid="badge-buy-inquiries-unread"
                  >
                    {myInquiryUnread}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {showInquiries && (
          <div className="mb-10" data-testid="buy-inquiries-section">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <Inbox className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900" data-testid="text-buy-inquiries-heading">My Inquiries</h2>
                <p className="text-xs text-gray-500">Conversations you've started with sellers on the marketplace.</p>
              </div>
            </div>
            {currentUserId ? (
              <BuyerInquiriesView
                currentUserId={currentUserId}
                initialInquiryId={initialInquiryId}
                viewListingHref={(listingId) => `/app/buy/listing/${listingId}`}
                emptyDescription="Browse PadPulse Marketplace listings on this page and click 'Inquire about this property' to start a conversation."
              />
            ) : (
              <div className="text-sm text-gray-500">Loading...</div>
            )}
          </div>
        )}

        {showMarketplace && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900" data-testid="text-marketplace-heading" data-tour="buy-marketplace-section">PadPulse Marketplace</h2>
                <p className="text-xs text-gray-500">Properties listed for sale by PadPulse hosts</p>
              </div>
              {marketplaceListings && marketplaceListings.length > 0 && (
                <Badge variant="secondary" className="ml-auto text-xs">{marketplaceListings.length} available</Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {marketplaceLoading ? (
                Array.from({ length: 3 }).map((_, i) => <DealCardSkeleton key={i} />)
              ) : marketplaceListings && marketplaceListings.length > 0 ? (
                marketplaceListings.map((listing) => (
                  <MarketplaceListingCard
                    key={listing.id}
                    listing={listing}
                    copiedPropertyId={batchResults[`marketplace:${listing.id}`]}
                  />
                ))
              ) : (
                <div className="col-span-full border border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400" data-testid="empty-marketplace-list">
                  <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No host listings yet</p>
                  <p className="text-sm mt-1">PadPulse hosts can list their properties for sale under the Sell tab.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {showWholesale && wholesaleListings && wholesaleListings.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Tag className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900" data-testid="text-wholesale-heading">Wholesale Deals</h2>
                <p className="text-xs text-gray-500">Submitted by verified wholesaler partners</p>
              </div>
              <Badge variant="secondary" className="ml-auto text-xs">{wholesaleListings.length} available</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {wholesaleLoading ? (
                Array.from({ length: 3 }).map((_, i) => <DealCardSkeleton key={i} />)
              ) : (
                wholesaleListings.map(listing => (
                  <WholesaleCard
                    key={listing.id}
                    listing={listing}
                    onInterested={setInterestedListing}
                    copiedPropertyId={batchResults[`wholesaler:${listing.id}`]}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {showWholesale && (
          <>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-100 dark:border-blue-900/50 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <h2 className="font-semibold text-blue-900 dark:text-blue-100 text-lg mb-1" data-testid="text-coming-soon">PadPulse Deals Coming Soon</h2>
                  <p className="text-sm text-blue-700 dark:text-blue-200 leading-relaxed">
                    We're partnering with local providers in Dallas, Tampa, and Atlanta to source turnkey PadSplit-ready investment properties.
                    Below are preview listings to give you an idea of the deals we'll be bringing to the platform.
                    Stay tuned — live deal flow is launching soon.
                  </p>
                </div>
              </div>
            </div>

            {!dealsLoading && deals && (
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-gray-500" data-testid="text-deal-count">{deals.length} preview deals</span>
                <span className="text-gray-300">·</span>
                <span className="text-sm text-gray-500">{uniqueMarkets.length} markets</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-10">
              {dealsLoading ? (
                Array.from({ length: 6 }).map((_, i) => <DealCardSkeleton key={i} />)
              ) : deals && deals.length > 0 ? (
                deals.map((deal) => <DealCard key={deal.id} deal={deal} copiedPropertyId={batchResults[`buydeal:${deal.id}`]} />)
              ) : (
                <div className="col-span-full text-center py-12 text-gray-400">
                  <Home className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">No deals available yet</p>
                  <p className="text-sm">Check back soon for new investment opportunities.</p>
                </div>
              )}
            </div>
          </>
        )}

        {showRealtor && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-testid="text-realtor-listings-heading" data-tour="buy-realtor-section">
                <Award className="h-5 w-5 text-violet-500" />
                Realtor Listings
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-gray-100 rounded-full p-0.5" data-testid="realtor-view-toggle">
                  <button
                    type="button"
                    onClick={() => setRealtorView("list")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      realtorView === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                    aria-pressed={realtorView === "list"}
                    data-testid="button-realtor-view-list"
                  >
                    <List className="h-3.5 w-3.5" /> List
                  </button>
                  <button
                    type="button"
                    onClick={() => setRealtorView("map")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      realtorView === "map" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                    aria-pressed={realtorView === "map"}
                    data-testid="button-realtor-view-map"
                  >
                    <MapIcon className="h-3.5 w-3.5" /> Map
                  </button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  data-testid="link-find-realtor-inline"
                >
                  <Link href="/app/find-realtor">
                    Find a realtor <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
            {realtorListings && realtorListings.length > 0 && (
              <div
                className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                data-testid="realtor-filters"
              >
                <div className="flex flex-col gap-1 min-w-[180px]">
                  <Label className="text-xs text-gray-600" htmlFor="realtor-city-filter">City / State</Label>
                  <Select value={realtorCityFilter} onValueChange={setRealtorCityFilter}>
                    <SelectTrigger id="realtor-city-filter" className="h-9 bg-white" data-testid="select-realtor-city">
                      <SelectValue placeholder="All cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="select-realtor-city-all">All cities</SelectItem>
                      {realtorCityOptions.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`select-realtor-city-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-600" htmlFor="realtor-min-price">Min price</Label>
                  <Input
                    id="realtor-min-price"
                    type="number"
                    inputMode="numeric"
                    placeholder="No min"
                    value={realtorMinPrice}
                    onChange={(e) => setRealtorMinPrice(e.target.value)}
                    className="h-9 w-32 bg-white"
                    data-testid="input-realtor-min-price"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-600" htmlFor="realtor-max-price">Max price</Label>
                  <Input
                    id="realtor-max-price"
                    type="number"
                    inputMode="numeric"
                    placeholder="No max"
                    value={realtorMaxPrice}
                    onChange={(e) => setRealtorMaxPrice(e.target.value)}
                    className="h-9 w-32 bg-white"
                    data-testid="input-realtor-max-price"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-600" htmlFor="realtor-min-beds">Min beds</Label>
                  <Select value={realtorMinBeds} onValueChange={setRealtorMinBeds}>
                    <SelectTrigger id="realtor-min-beds" className="h-9 w-28 bg-white" data-testid="select-realtor-min-beds">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" data-testid="select-realtor-min-beds-any">Any</SelectItem>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)} data-testid={`select-realtor-min-beds-${n}`}>{n}+</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-600" htmlFor="realtor-min-baths">Min baths</Label>
                  <Select value={realtorMinBaths} onValueChange={setRealtorMinBaths}>
                    <SelectTrigger id="realtor-min-baths" className="h-9 w-28 bg-white" data-testid="select-realtor-min-baths">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" data-testid="select-realtor-min-baths-any">Any</SelectItem>
                      {["1", "1.5", "2", "2.5", "3", "4"].map((n) => (
                        <SelectItem key={n} value={n} data-testid={`select-realtor-min-baths-${n}`}>{n}+</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1 min-w-[160px]">
                  <Label className="text-xs text-gray-600" htmlFor="realtor-listing-type">Listing type</Label>
                  <Select value={realtorListingType} onValueChange={setRealtorListingType}>
                    <SelectTrigger id="realtor-listing-type" className="h-9 bg-white" data-testid="select-realtor-listing-type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="select-realtor-listing-type-all">All types</SelectItem>
                      <SelectItem value="mls" data-testid="select-realtor-listing-type-mls">MLS</SelectItem>
                      <SelectItem value="off_market" data-testid="select-realtor-listing-type-off_market">Off Market</SelectItem>
                      <SelectItem value="pocket" data-testid="select-realtor-listing-type-pocket">Pocket</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {realtorFiltersActive && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearRealtorFilters}
                    className="h-9"
                    data-testid="button-clear-realtor-filters"
                  >
                    Clear
                  </Button>
                )}
                <span className="ml-auto text-xs text-gray-500" data-testid="text-realtor-filter-count">
                  {filteredRealtorListings.length} of {realtorListings.length}
                </span>
              </div>
            )}
            {realtorView === "map" ? (
              realtorLoading ? (
                <Skeleton className="h-[500px] w-full rounded-lg" data-testid="realtor-map-skeleton" />
              ) : filteredRealtorListings.length > 0 ? (
                <RealtorListingsMap listings={filteredRealtorListings} />
              ) : (
                <div className="border border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400" data-testid="empty-realtor-map">
                  <MapIcon className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">
                    {realtorFiltersActive ? "No realtor listings match your filters" : "No realtor listings to map yet"}
                  </p>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {realtorLoading ? (
                  Array.from({ length: 3 }).map((_, i) => <DealCardSkeleton key={i} />)
                ) : filteredRealtorListings.length > 0 ? (
                  filteredRealtorListings.map((listing) => <RealtorListingCard key={listing.id} listing={listing} copiedPropertyId={batchResults[`realtor:${listing.id}`]} />)
                ) : (
                  <div className="col-span-full border border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400" data-testid="empty-realtor-list">
                    <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">
                      {realtorFiltersActive ? "No realtor listings match your filters" : "No realtor listings yet"}
                    </p>
                    {!realtorFiltersActive && (
                      <p className="text-sm mt-1">Partner realtors will list MLS, off-market, and pocket deals here.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <InterestedDialog listing={interestedListing} onClose={() => setInterestedListing(null)} />
    </AppLayout>
  );
}
